"use client";

import { useState } from "react";
import { CopyButton } from "@/components/CopyButton";
import { site } from "@/lib/site";

const RAW_BASE = "https://raw.githubusercontent.com/Raktim94/KinetiRx/main/scripts";

const OS_TABS = [
  {
    id: "deb",
    label: "Ubuntu / Debian",
    command: `curl -fsSL ${site.installLinuxUrl} | sudo bash`,
    note: "Package for Ubuntu 24.04 and Debian 12 or newer, amd64 and arm64, with systemd. It sets up Docker if needed, runs KinetiRx in Docker and starts it. Read the script first, or download the .deb below and run sudo apt install ./file.deb.",
    downloads: [["Download .deb (amd64)", site.debAmd64Url], ["Download .deb (arm64)", site.debArm64Url]],
  },
  {
    id: "mac",
    label: "macOS",
    command: `curl -fsSL ${RAW_BASE}/quickstart.sh | bash`,
    note: "Installs Docker Desktop and git via Homebrew if either is missing.",
  },
  {
    id: "linux",
    label: "Linux",
    command: `curl -fsSL ${RAW_BASE}/quickstart.sh | bash`,
    note: "Installs Docker Engine via the official get.docker.com script and git via your package manager, if either is missing.",
  },
  {
    id: "windows",
    label: "Windows",
    command: `irm ${RAW_BASE}/quickstart.ps1 | iex`,
    note: "Run in PowerShell. No Docker Desktop — installs WSL2 (Windows' own free Linux subsystem) with a real Docker Engine + Docker CLI inside it if needed, plus the native Windows Docker CLI via winget.",
  },
] as const;

export function InstallCommand({ className }: { className?: string }) {
  const [activeId, setActiveId] = useState<(typeof OS_TABS)[number]["id"]>("deb");
  const active = OS_TABS.find((os) => os.id === activeId)!;

  return (
    <div className={className}>
      <div role="tablist" aria-label="Operating system" className="mb-2 inline-flex gap-1 rounded-lg border border-[var(--line)] bg-white p-1 flex-wrap max-w-full">
        {OS_TABS.map((os) => (
          <button
            key={os.id}
            type="button"
            role="tab"
            aria-selected={os.id === activeId}
            onClick={() => setActiveId(os.id)}
            className={`rounded-md px-3 py-1 text-xs font-semibold transition ${
              os.id === activeId ? "bg-brand-700 text-white" : "text-[var(--ink-muted)] hover:text-[var(--ink)]"
            }`}
          >
            {os.label}
          </button>
        ))}
      </div>

      <div className="cmd-block flex items-center gap-3">
        <span className="prompt">$</span>
        <div className="min-w-0 flex-1 overflow-x-auto">
          <span className="select-all whitespace-nowrap">{active.command}</span>
        </div>
        <CopyButton text={active.command} />
      </div>
      <p className="mt-2 text-xs text-[var(--ink-muted)]">{active.note}</p>
      {"downloads" in active && (
        <p className="mt-3 flex flex-wrap gap-2">
          {active.downloads.map(([label, href]) => (
            <a key={href} href={href} className="inline-flex items-center rounded-lg bg-brand-700 px-3 py-2 text-xs font-semibold text-white transition hover:bg-brand-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700">
              {label}
            </a>
          ))}
          <a href="/install-linux.sh" className="inline-flex items-center rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-800 transition hover:bg-slate-50">Read the script</a>
          <span className="inline-flex items-center text-xs text-slate-700">
            v{site.debVersion} · <a className="ml-1 underline underline-offset-2" href={`https://github.com/Raktim94/KinetiRx/releases/tag/v${site.debVersion}`}>release notes</a>
          </span>
        </p>
      )}
    </div>
  );
}
